import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { entityFormAdapters, type ReleaseFormValues } from '@/features/forms';
import {
  buildStatusMetricCards,
  mapStatusOptionsToMetricOptions,
  resolveMetricTone,
} from '@/features/metrics/components/statusMetricCards';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { buildReleaseCounts } from '@/features/quality/components/qualityStatusDataHelpers';
import { queryReleaseSummary } from '@/features/releases/data';
import { useCreateRelease } from '@/features/releases/hooks/releases';
import { useScopedProjectFilter } from '@/features/shared/hooks/useScopedProjectFilter';
import { buildDeliveryPageFilters } from '@/features/shared/utils/deliveryPageFilters';
import { useNotifications } from '@/providers/notificationHooks';
import { releaseStatusOptions } from '@/shared/constants/statusOptions';
import { getToastMessage } from '@/shared/copy/toasts';
import {
  queryKeys,
  useIsAuthenticated,
  useMutationErrorSnackbar,
  useOrganisationEntitlements,
  usePersistedStringArray,
  useTagOptions,
} from '@/shared/hooks';
import {
  buildContainsFilter,
  buildEqFilter,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { buildGridQueryInput, type GridQueryInput } from '@/ui/composites/grid/gridUtils';
import { useDialogFormState } from '@/ui/hooks/useDialogFormState';
import { useFormErrors } from '@/ui/hooks/useFormErrors';
import type { TagOption } from '@/shared/data/tags';
import type { FieldErrorProps } from '@/ui/hooks/useFormErrors';
import type { ReleaseStatus } from '@flowqa/shared';

type ReleaseAddDialogState = {
  open: boolean;
  saving: boolean;
  values: ReleaseFormValues;
  errors: { name?: FieldErrorProps; projectId?: FieldErrorProps };
  errorMessage: string | null;
  projectOptions: Array<{ id: string; name: string }>;
  showProjectSelect: boolean;
  showStatusSelect: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSave: () => void;
  handlers: {
    onNameChange: (value: string) => void;
    onStatusChange: (value: ReleaseStatus) => void;
    onProjectChange: (value: string) => void;
    onPlannedForChange: (value: string) => void;
    onReleasedAtChange: (value: string) => void;
    onTagsChange: (value: string[]) => void;
  };
};

export type ReleaseDeliveryMode = 'inProgress' | 'released';

const inProgressReleaseStatuses: ReleaseStatus[] = ['draft', 'planned', 'ready', 'overdue'];
const releasedReleaseStatuses: ReleaseStatus[] = ['released'];

const releaseModeStatuses: Record<ReleaseDeliveryMode, ReleaseStatus[]> = {
  inProgress: inProgressReleaseStatuses,
  released: releasedReleaseStatuses,
};

type UseReleasesPageViewModelResult = {
  activeMode: ReleaseDeliveryMode;
  setActiveMode: (mode: ReleaseDeliveryMode) => void;
  modeCounts: Record<ReleaseDeliveryMode, number>;
  searchValue: string;
  setSearchValue: (value: string) => void;
  selectedStatuses: ReleaseStatus[];
  selectedTags: string[];
  selectedProjects: string[];
  tagOptions: TagOption[];
  tagOptionsLoading: boolean;
  projectFilterOptions: Array<{ value: string; label: string }>;
  showProjectFilter: boolean;
  onStatusToggle: (status: ReleaseStatus) => void;
  onStatusesChange: (nextStatuses: ReleaseStatus[]) => void;
  onTagsChange: (nextTags: string[]) => void;
  onProjectsChange: (nextProjects: string[]) => void;
  tableFilters: GridQueryInput['filters'];
  releaseMetricCards: ReturnType<typeof buildStatusMetricCards>;
  releaseMetricCardsLoading: boolean;
  showPageLoading: boolean;
  isKnownEmpty: boolean;
  addDialog: ReleaseAddDialogState;
};

export const useReleasesPageViewModel = (): UseReleasesPageViewModelResult => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const {
    effectiveProjectId,
    handleProjectsChange,
    projectOptions,
    projectFilterOptions,
    selectedSidebarProjectId,
    selectedProjects,
    showProjectFilter,
  } = useScopedProjectFilter({ enabled: selectedOrganisationId !== null });
  const [selectedStatuses, setSelectedStatuses] = useState<ReleaseStatus[]>([]);
  const [selectedTags, setSelectedTags] = usePersistedStringArray('tag-filter:releases');
  const [activeMode, setActiveModeState] = useState<ReleaseDeliveryMode>('inProgress');
  const [searchValue, setSearchValue] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const addForm = useDialogFormState<ReleaseFormValues>({
    name: '',
    projectId: effectiveProjectId ?? '',
    status: 'draft',
    plannedFor: '',
    releasedAt: '',
    tags: [],
  });
  const addErrors = useFormErrors(addForm);
  const mutationError = useMutationErrorSnackbar();
  const createRelease = useCreateRelease();
  const { notify } = useNotifications();
  const { tagOptions, loading: tagOptionsLoading } = useTagOptions({
    projectId: effectiveProjectId,
    enabled: isAuthenticated,
  });
  const entitlementsQuery = useOrganisationEntitlements(selectedOrganisationId ?? null);
  const deliveryReady = selectedOrganisationId === null || entitlementsQuery.isSuccess;
  const deliveryEnabled =
    selectedOrganisationId === null
      ? true
      : (entitlementsQuery.data?.addOns.deliveryEnabled ?? false);
  const showDelta = false;
  const hasActiveFilters =
    searchValue.trim() !== '' ||
    selectedStatuses.length > 0 ||
    selectedTags.length > 0 ||
    (showProjectFilter && selectedProjects.length > 0);
  const activeModeStatuses = releaseModeStatuses[activeMode];
  const modeStatusSet = useMemo(() => new Set(activeModeStatuses), [activeModeStatuses]);
  const tableStatusValues = useMemo(
    () =>
      selectedStatuses.length > 0
        ? selectedStatuses.filter((status) => modeStatusSet.has(status))
        : activeModeStatuses,
    [activeModeStatuses, modeStatusSet, selectedStatuses],
  );

  const searchFilters = useMemo(() => buildContainsFilter('name', searchValue), [searchValue]);
  const tagFilters = useMemo(() => buildInFilter('tags', selectedTags), [selectedTags]);
  const projectFilters = useMemo(
    () => (showProjectFilter ? buildInFilter('projectId', selectedProjects) : []),
    [selectedProjects, showProjectFilter],
  );
  const statusFilters = useMemo(
    () => buildInFilter('status', selectedStatuses),
    [selectedStatuses],
  );
  const tableStatusFilters = useMemo(
    () =>
      selectedStatuses.length > 0 && tableStatusValues.length === 0
        ? buildEqFilter('id', '__no_matching_release_status__')
        : buildInFilter('status', tableStatusValues),
    [selectedStatuses.length, tableStatusValues],
  );
  const { summaryFilters } = useMemo(
    () =>
      buildDeliveryPageFilters({
        rangeStartIso: null,
        rangeEndIso: null,
        previousRangeStartIso: null,
        previousRangeEndIso: null,
        statusFilters: mergeFilters(statusFilters, searchFilters),
        tagFilters,
        projectFilters,
      }),
    [projectFilters, searchFilters, statusFilters, tagFilters],
  );
  const releaseSummaryInput = useMemo(
    () =>
      buildGridQueryInput({
        page: 1,
        pageSize: 1,
        sorts: [],
        filters: summaryFilters,
      }),
    [summaryFilters],
  );
  const releaseSummaryFiltersKey = useMemo(
    () => JSON.stringify(releaseSummaryInput.filters),
    [releaseSummaryInput.filters],
  );
  const releaseSummaryQuery = useQuery({
    queryKey: queryKeys.releaseSummary(
      effectiveProjectId,
      selectedOrganisationId ?? null,
      releaseSummaryFiltersKey,
    ),
    enabled: isAuthenticated && deliveryReady && deliveryEnabled,
    queryFn: async () =>
      queryReleaseSummary(
        effectiveProjectId,
        selectedOrganisationId ?? undefined,
        releaseSummaryInput,
      ),
  });
  const releaseSummary = releaseSummaryQuery.data ?? null;
  const showPageLoading =
    releaseSummaryQuery.isLoading && releaseSummary === null && !releaseSummaryQuery.isError;
  const isKnownEmpty =
    !showPageLoading &&
    deliveryEnabled &&
    releaseSummary !== null &&
    releaseSummary.total === 0 &&
    !hasActiveFilters;
  const releaseCounts = useMemo(() => buildReleaseCounts(releaseSummary), [releaseSummary]);
  const modeCounts = useMemo(() => {
    const countStatuses = (statuses: ReleaseStatus[]): number =>
      statuses
        .filter((status) => selectedStatuses.length === 0 || selectedStatuses.includes(status))
        .reduce((total, status) => total + (releaseCounts[status] ?? 0), 0);

    return {
      inProgress: countStatuses(inProgressReleaseStatuses),
      released: countStatuses(releasedReleaseStatuses),
    };
  }, [releaseCounts, selectedStatuses]);
  const releaseMetricOptions = useMemo(
    () => mapStatusOptionsToMetricOptions(releaseStatusOptions, resolveMetricTone),
    [],
  );
  const releaseMetricCards = useMemo(
    () =>
      buildStatusMetricCards({
        options: releaseMetricOptions,
        counts: releaseCounts,
        deltas: undefined,
        total: releaseSummary?.total,
        totalLabel: 'Releases',
        previousCounts: {},
        previousTotal: undefined,
        loading: releaseSummaryQuery.isFetching && !releaseSummaryQuery.isError,
        showDelta,
      }),
    [
      releaseCounts,
      releaseMetricOptions,
      releaseSummary?.total,
      releaseSummaryQuery.isError,
      releaseSummaryQuery.isFetching,
      showDelta,
    ],
  );
  const tableFilters = useMemo(
    () => mergeFilters(searchFilters, tagFilters, projectFilters, tableStatusFilters),
    [projectFilters, searchFilters, tableStatusFilters, tagFilters],
  );
  const onStatusToggle = useCallback((status: ReleaseStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((item) => item !== status) : [...prev, status],
    );
  }, []);
  const onStatusesChange = useCallback((nextStatuses: ReleaseStatus[]) => {
    setSelectedStatuses(nextStatuses);
  }, []);
  const setActiveMode = useCallback((mode: ReleaseDeliveryMode) => {
    setActiveModeState(mode);
  }, []);

  const closeAddDialog = useCallback(() => {
    addForm.handleClose(
      () => {
        setAddOpen(false);
      },
      { clearError: mutationError.clearError },
    );
  }, [addForm, mutationError.clearError]);

  const openAddDialog = useCallback(() => {
    const defaultProjectId =
      selectedSidebarProjectId ??
      (selectedProjects.length === 1 ? (selectedProjects[0] ?? '') : '');
    addForm.setField('name', '');
    addForm.setField('projectId', defaultProjectId);
    addForm.setField('status', activeMode === 'released' ? 'released' : 'draft');
    addForm.setField('plannedFor', '');
    addForm.setField('releasedAt', '');
    addForm.setField('tags', []);
    mutationError.clearError();
    setAddOpen(true);
  }, [activeMode, addForm, mutationError, selectedProjects, selectedSidebarProjectId]);

  const saveRelease = useCallback(() => {
    const valid = selectedSidebarProjectId
      ? addForm.validateRequired('name')
      : addForm.validateRequired('name', 'projectId');
    if (!valid) return;
    const projectId = selectedSidebarProjectId ?? addForm.values.projectId;
    const payload = entityFormAdapters.release.toCreatePayload(addForm.values, projectId);
    createRelease.mutate(payload, {
      onSuccess: () => {
        addForm.handleClose(
          () => {
            setAddOpen(false);
          },
          { clearError: mutationError.clearError },
        );
        notify({
          message: getToastMessage('entity.created', { entity: 'release', name: payload.name }),
          tone: 'green',
        });
      },
      onError: (err) => {
        if (!addForm.applyApiErrors(err)) {
          mutationError.setError(err);
          return;
        }
        mutationError.clearError();
      },
    });
  }, [addForm, createRelease, mutationError, notify, selectedSidebarProjectId]);

  return {
    activeMode,
    setActiveMode,
    modeCounts,
    searchValue,
    setSearchValue,
    selectedStatuses,
    selectedTags,
    selectedProjects,
    tagOptions,
    tagOptionsLoading,
    projectFilterOptions,
    showProjectFilter,
    onStatusToggle,
    onStatusesChange,
    onTagsChange: setSelectedTags,
    onProjectsChange: handleProjectsChange,
    tableFilters,
    releaseMetricCards,
    releaseMetricCardsLoading: releaseSummaryQuery.isFetching && !releaseSummaryQuery.isError,
    showPageLoading,
    isKnownEmpty,
    addDialog: {
      open: addOpen,
      saving: createRelease.isPending,
      values: addForm.values,
      errors: {
        name: addErrors.fieldErrorProps('name'),
        projectId: addErrors.fieldErrorProps('projectId'),
      },
      errorMessage: addForm.submitError,
      projectOptions,
      showProjectSelect: selectedSidebarProjectId === null,
      showStatusSelect: true,
      onOpen: openAddDialog,
      onClose: closeAddDialog,
      onSave: saveRelease,
      handlers: {
        onNameChange: (value) => {
          addForm.setField('name', value);
        },
        onStatusChange: (value) => {
          addForm.setField('status', value);
        },
        onProjectChange: (value) => {
          addForm.setField('projectId', value);
        },
        onPlannedForChange: (value) => {
          addForm.setField('plannedFor', value);
        },
        onReleasedAtChange: (value) => {
          addForm.setField('releasedAt', value);
        },
        onTagsChange: (value: string[]) => {
          addForm.setField('tags', value);
        },
      },
    },
  };
};
